/**
 * DATA LAYER (DL): TUG OF WAR (ESTIRA Y AFLOJA)
 * Módulo de comunicación REST nativa (Fetch API) con el backend de Interaktia-V2 para la dinámica TugOfWar.
 * Gestiona el CRUD de temas visuales, configuración de sala, historial de partidas disputadas,
 * subida y procesamiento multimedia, comandos autoritativos de control en tiempo real y telemetría de aficionados.
 * 
 * @module utilities/DL/TugOfWar
 */

import SignalRService from '../WS/SignalRService';

/**
 * Genera o recupera un identificador único persistente para el dispositivo del aficionado.
 * Almacena el identificador en localStorage o sessionStorage para garantizar trazabilidad.
 * @returns {string} ID único de cliente aficionado persistente.
 */
export const getFanClientId = () => {
    if (typeof localStorage !== 'undefined') {
        let clientId = localStorage.getItem('tugofwar_fan_client_id');
        if (!clientId) {
            clientId = 'fan_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
            localStorage.setItem('tugofwar_fan_client_id', clientId);
        }
        return clientId;
    }
    if (typeof sessionStorage !== 'undefined') {
        let clientId = sessionStorage.getItem('tugofwar_fan_client_id');
        if (!clientId) {
            clientId = 'fan_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
            sessionStorage.setItem('tugofwar_fan_client_id', clientId);
        }
        return clientId;
    }
    return 'fan_' + Math.random().toString(36).substring(2, 15);
};

/**
 * Resuelve y retorna la URL base para los endpoints del controlador REST de TugOfWar.
 * Sanitiza barras diagonales redundantes y respeta la configuración de variables de entorno.
 * @returns {string} URL base formateada para el controlador de TugOfWar.
 */
const getApiBaseUrl = () => {
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const cleanUrl = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;
    return `${cleanUrl}/TugOfWar`;
};

// =========================================================================
// CRUD DE TEMAS VISUALES
// =========================================================================

/**
 * Obtiene la lista de temas visuales disponibles para Estira y Afloja con soporte de filtrado multi-tenant.
 * @param {string|null} [roomId=null] - Identificador GUID único de la sala (opcional).
 * @param {string|null} [operatorId=null] - Identificador GUID único del operador (opcional).
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Respuesta estructurada con la colección de temas.
 */
export const GetThemesDL = async (roomId = null, operatorId = null) => {
    try {
        const queryParameters = new URLSearchParams();
        if (roomId) {
            queryParameters.append('roomId', String(roomId).trim());
        }
        if (operatorId) {
            queryParameters.append('operatorId', String(operatorId).trim());
        }

        const queryString = queryParameters.toString();
        const requestUrl = `${getApiBaseUrl()}/themes${queryString ? `?${queryString}` : ''}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar los temas de TugOfWar.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red o conexión al consultar los temas de TugOfWar.'
        };
    }
};

/**
 * Obtiene el detalle completo de un tema de Estira y Afloja por su identificador único.
 * @param {number|string} themeIdentifier - Identificador numérico único del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema visual obtenido.
 */
export const GetThemeByIdDL = async (themeIdentifier) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier).trim());
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener el tema ${themeIdentifier}.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al consultar el tema con ID ${themeIdentifier}.`
        };
    }
};

/**
 * Crea un nuevo tema visual personalizado para Estira y Afloja.
 * @param {Object} themePayload - Objeto DTO con los datos del nuevo tema visual.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado y confirmado por el backend.
 */
export const CreateThemeDL = async (themePayload) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/theme`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(themePayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al crear el tema de TugOfWar.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema creado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al registrar el tema de TugOfWar.'
        };
    }
};

/**
 * Actualiza la configuración gráfica y avatares de un tema existente de Estira y Afloja.
 * @param {number|string} themeIdentifier - Identificador numérico único del tema a modificar.
 * @param {Object} themePayload - Datos actualizados del tema visual.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema actualizado y confirmado.
 */
export const UpdateThemeDL = async (themeIdentifier, themePayload) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier).trim());
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(themePayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al actualizar el tema ${themeIdentifier}.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema actualizado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al actualizar el tema con ID ${themeIdentifier}.`
        };
    }
};

/**
 * Elimina un tema personalizado de Estira y Afloja (los presets del sistema están protegidos contra eliminación).
 * @param {number|string} themeIdentifier - Identificador numérico del tema a eliminar.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación del tema.
 */
export const DeleteThemeDL = async (themeIdentifier) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier).trim());
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al eliminar el tema ${themeIdentifier}.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({}));
        return {
            success: true,
            message: responseData.message || 'Tema eliminado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al eliminar el tema con ID ${themeIdentifier}.`
        };
    }
};

/**
 * Clona un tema existente como una nueva copia editable e independiente de Estira y Afloja.
 * @param {number|string} themeIdentifier - Identificador numérico del tema origen a duplicar.
 * @param {string|null} [newThemeName=null] - Nombre opcional para la nueva copia del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado resultante.
 */
export const CloneThemeDL = async (themeIdentifier, newThemeName = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier).trim());
        const queryParams = new URLSearchParams();
        if (newThemeName) {
            queryParams.append('newName', String(newThemeName).trim());
        }

        const queryString = queryParams.toString();
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}/clone${queryString ? `?${queryString}` : ''}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                newName: newThemeName ? String(newThemeName).trim() : null
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al clonar el tema ${themeIdentifier}.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema clonado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al clonar el tema con ID ${themeIdentifier}.`
        };
    }
};

// =========================================================================
// CONFIGURACIÓN E HISTORIAL DE SALA
// =========================================================================

/**
 * Obtiene la configuración activa de Estira y Afloja para una sala específica.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa de la sala.
 */
export const GetConfigDL = async (roomId) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId).trim());
        const requestUrl = `${getApiBaseUrl()}/config/${sanitizedRoomId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener la configuración de sala.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar la configuración de TugOfWar.'
        };
    }
};

/**
 * Guarda o actualiza la configuración de Estira y Afloja para una sala.
 * @param {Object} configPayload - Datos de configuración (roomId, activeThemeId, gameMode, displacementStyle, roundDurationSeconds, enableOvertimeOnDraw).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración confirmada y persistida.
 */
export const SaveConfigDL = async (configPayload) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/config`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(configPayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al guardar la configuración de sala.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Configuración guardada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar la configuración de TugOfWar.'
        };
    }
};

/**
 * Obtiene el historial de partidas disputadas en una sala.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number} [limit=20] - Cantidad máxima de partidas a retornar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Lista histórica de partidas.
 */
export const GetMatchHistoryDL = async (roomId, limit = 20) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId).trim());
        const sanitizedLimit = encodeURIComponent(String(limit || 20));
        const requestUrl = `${getApiBaseUrl()}/history/${sanitizedRoomId}?limit=${sanitizedLimit}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar el historial de partidas.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el historial de partidas de TugOfWar.'
        };
    }
};

/**
 * Registra y persiste las métricas y el resultado final de una partida de Estira y Afloja.
 * @param {Object} matchPayload - Métricas finales de la partida disputada.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado persistido en base de datos.
 */
export const SaveMatchResultDL = async (matchPayload) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/history`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(matchPayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al registrar el resultado de la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Resultado de la partida guardado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar el resultado de la partida de TugOfWar.'
        };
    }
};

// =========================================================================
// CARGA MULTIMEDIA Y ASSETS
// =========================================================================

/**
 * Sube y optimiza un recurso gráfico individual para TugOfWar en formato multipart/form-data.
 * @param {File|Blob|FormData} fileOrFormData - Archivo individual o instancia de FormData a transmitir.
 * @returns {Promise<{success: boolean, data?: Object, url?: string, message?: string}>} URL y metadata del recurso procesado.
 */
export const UploadAssetDL = async (fileOrFormData) => {
    try {
        let requestFormData = fileOrFormData;
        if (!(fileOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            requestFormData.append('file', fileOrFormData);
        }

        const requestUrl = `${getApiBaseUrl()}/upload`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            body: requestFormData
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al subir el archivo multimedia.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: Boolean(responseData.success !== false),
            data: responseData,
            url: responseData.url || (responseData.data && responseData.data.url),
            message: responseData.message || 'Archivo subido y procesado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al subir el archivo multimedia a TugOfWar.'
        };
    }
};

/**
 * Sube y optimiza un lote de archivos gráficos para avatares, fondos o texturas de TugOfWar.
 * Ejecuta subidas concurrentes y consolida el reporte de procesamiento.
 * @param {Array<File>|FileList|FormData} filesOrFormData - Colección de archivos o FormData para envío masivo.
 * @returns {Promise<{success: boolean, data?: Object, urls: Array<string>, files: Array<Object>, count: number, message?: string}>} Reporte de procesamiento del lote.
 */
export const UploadBatchMediaDL = async (filesOrFormData) => {
    try {
        let filesArray = [];
        if (filesOrFormData instanceof FormData) {
            filesArray = filesOrFormData.getAll('files');
            if (filesArray.length === 0) {
                filesArray = filesOrFormData.getAll('file');
            }
        } else if (Array.isArray(filesOrFormData) || (typeof FileList !== 'undefined' && filesOrFormData instanceof FileList)) {
            filesArray = Array.from(filesOrFormData);
        }

        if (filesArray.length === 0) {
            return {
                success: false,
                urls: [],
                files: [],
                count: 0,
                message: 'No se encontraron archivos válidos para la carga por lote.'
            };
        }

        const uploadPromises = filesArray.map(async (fileItem) => {
            const individualFormData = new FormData();
            individualFormData.append('file', fileItem);
            return await UploadAssetDL(individualFormData);
        });

        const uploadResults = await Promise.allSettled(uploadPromises);
        const successfulUploads = [];
        const filesReport = [];

        uploadResults.forEach((settledResult, itemIndex) => {
            if (settledResult.status === 'fulfilled' && settledResult.value && settledResult.value.success && settledResult.value.url) {
                successfulUploads.push(settledResult.value.url);
                filesReport.push({
                    index: itemIndex,
                    success: true,
                    url: settledResult.value.url,
                    data: settledResult.value.data
                });
            } else {
                filesReport.push({
                    index: itemIndex,
                    success: false,
                    message: settledResult.reason?.message || settledResult.value?.message || 'Error al procesar archivo.'
                });
            }
        });

        const hasAnySuccess = successfulUploads.length > 0;
        return {
            success: hasAnySuccess,
            urls: successfulUploads,
            files: filesReport,
            count: successfulUploads.length,
            message: hasAnySuccess
                ? `Se procesaron exitosamente ${successfulUploads.length} de ${filesArray.length} archivos.`
                : 'Ningún archivo del lote pudo ser procesado.'
        };
    } catch (networkError) {
        return {
            success: false,
            urls: [],
            files: [],
            count: 0,
            message: 'Error de red al procesar el lote multimedia de TugOfWar.'
        };
    }
};

// =========================================================================
// COMANDOS DE CONTROL DE SALA EN TIEMPO REAL
// =========================================================================

/**
 * Proyecta la portada de Estira y Afloja en la pantalla gigante y sincroniza a los aficionados conectados.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number|string} [themeIdentifier=0] - Identificador del tema visual a proyectar.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo actualizado de la partida.
 */
export const ShowCoverDL = async (roomId, themeIdentifier = 0) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/cover`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: String(roomId).trim(),
                themeId: Number(themeIdentifier) || 0
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al proyectar la portada.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Portada de Estira y Afloja proyectada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al proyectar la portada de TugOfWar.'
        };
    }
};

/**
 * Proyecta el escenario de juego de Estira y Afloja preparando los equipos, soga y charco de lodo.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number|string} [themeIdentifier=0] - Identificador del tema visual activo.
 * @param {string} [gameMode='Timed'] - Modo de juego ('Timed' | 'SuddenDeath').
 * @param {string} [displacementStyle='DynamicBias'] - Estilo de física de soga ('DynamicBias' | 'VisualSuspense').
 * @param {number} [durationSeconds=15] - Duración configurada de la ronda en segundos.
 * @param {boolean} [enableOvertimeOnDraw=false] - Habilita tiempo extra / muerte súbita en caso de empate al sonar el timbre.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo de la escena preparada.
 */
export const ShowGameSceneDL = async (
    roomId,
    themeIdentifier = 0,
    gameMode = 'Timed',
    displacementStyle = 'DynamicBias',
    durationSeconds = 15,
    enableOvertimeOnDraw = false
) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/game-scene`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: String(roomId).trim(),
                themeId: Number(themeIdentifier) || 0,
                gameMode: gameMode || 'Timed',
                displacementStyle: displacementStyle || 'DynamicBias',
                durationSeconds: Number(durationSeconds) || 15,
                enableOvertimeOnDraw: Boolean(enableOvertimeOnDraw)
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al preparar la escena de juego.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Escenario de juego de Estira y Afloja preparado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al preparar el escenario de juego de TugOfWar.'
        };
    }
};

/**
 * Inicia la cuenta regresiva sincronizada (3, 2, 1) y posterior arranque de la partida con telemetría a 100ms.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la partida iniciada.
 */
export const StartCountdownDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/start`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: String(roomId).trim() })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al iniciar la cuenta regresiva.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Cuenta regresiva y partida iniciadas.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al iniciar la partida de TugOfWar.'
        };
    }
};

/**
 * Pausa temporalmente la partida activa conservando el tiempo transcurrido y la posición de la soga.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de pausa.
 */
export const PauseMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/pause`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: String(roomId).trim() })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al pausar la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida pausada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al pausar la partida de TugOfWar.'
        };
    }
};

/**
 * Reanuda una partida pausada continuando el cronómetro autoritativo y la física de la soga.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de reanudación.
 */
export const ResumeMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/resume`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: String(roomId).trim() })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al reanudar la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida reanudada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al reanudar la partida de TugOfWar.'
        };
    }
};

/**
 * Finaliza anticipadamente la partida activa, calcula el ganador o K.O. y persiste en base de datos.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado final calculado.
 */
export const FinishEarlyDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/finish-early`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: String(roomId).trim() })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al finalizar la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida finalizada anticipadamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al finalizar anticipadamente la partida de TugOfWar.'
        };
    }
};

/**
 * Reinicia la partida de la sala devolviéndola al estado inactivo con soga centrada y contadores en cero.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/reset`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ roomId: String(roomId).trim() })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al reiniciar la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida reiniciada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al reiniciar la partida de TugOfWar.'
        };
    }
};

/**
 * Consulta el estado actual en memoria RAM de la partida para una sala específica.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado autoritativo en vivo de la partida.
 */
export const GetMatchStateDL = async (roomId) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId).trim());
        const requestUrl = `${getApiBaseUrl()}/state/${sanitizedRoomId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar el estado de la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el estado de la partida de TugOfWar.'
        };
    }
};

// =========================================================================
// TELEMETRÍA Y AFICIONADOS (FANS)
// =========================================================================

/**
 * Envía un lote acumulado de toques (taps) emitidos por un aficionado móvil.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} playerChoice - Bando elegido por el aficionado ('player1' | 'player2').
 * @param {number} [tapCount=1] - Cantidad de toques acumulados en la ventana de transmisión.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión del aficionado.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado tras procesar los taps.
 */
export const SendTapBatchDL = async (roomId, playerChoice, tapCount = 1, connectionId = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/tap-batch`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: String(roomId).trim(),
                playerChoice: playerChoice || 'player1',
                tapCount: Number(tapCount) || 1,
                connectionId: activeConnectionId
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al transmitir el lote de taps.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({ success: true }));
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al enviar el lote de taps de TugOfWar.'
        };
    }
};

/**
 * Registra formalmente la elección de bando de un aficionado ("player1" o "player2") para el cálculo del promedio per cápita.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} playerChoice - Bando seleccionado ('player1' | 'player2').
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de registro de aficionado.
 */
export const RegisterFanDL = async (roomId, playerChoice, connectionId = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/fan/register`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: String(roomId).trim(),
                playerChoice: playerChoice || 'player1',
                connectionId: activeConnectionId
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al registrar aficionado.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Aficionado registrado al equipo exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al registrar aficionado en TugOfWar.'
        };
    }
};

/**
 * Desregistra un aficionado cuando se desconecta o abandona la vista interactiva.
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión del aficionado.
 * @param {string} [playerChoice='player1'] - Bando del que se desvincula.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de desregistro.
 */
export const UnregisterFanDL = async (roomId, connectionId = '', playerChoice = 'player1') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/fan/unregister`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                roomId: String(roomId).trim(),
                connectionId: activeConnectionId,
                playerChoice: playerChoice || 'player1'
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al desregistrar aficionado.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({}));
        return {
            success: true,
            message: responseData.message || 'Aficionado desregistrado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al desregistrar aficionado en TugOfWar.'
        };
    }
};

// Aliases de compatibilidad
export const GetThemesTugOfWarDL = GetThemesDL;
export const GetThemeByIdTugOfWarDL = GetThemeByIdDL;
export const CreateThemeTugOfWarDL = CreateThemeDL;
export const UpdateThemeTugOfWarDL = UpdateThemeDL;
export const DeleteThemeTugOfWarDL = DeleteThemeDL;
export const CloneThemeTugOfWarDL = CloneThemeDL;
export const GetConfigTugOfWarDL = GetConfigDL;
export const SaveConfigTugOfWarDL = SaveConfigDL;
export const GetMatchHistoryTugOfWarDL = GetMatchHistoryDL;
export const SaveMatchResultTugOfWarDL = SaveMatchResultDL;
export const UploadAssetTugOfWarDL = UploadAssetDL;
export const UploadBatchMediaTugOfWarDL = UploadBatchMediaDL;
export const ShowCoverTugOfWarDL = ShowCoverDL;
export const ShowGameSceneTugOfWarDL = ShowGameSceneDL;
export const StartCountdownTugOfWarDL = StartCountdownDL;
export const PauseMatchTugOfWarDL = PauseMatchDL;
export const ResumeMatchTugOfWarDL = ResumeMatchDL;
export const FinishEarlyTugOfWarDL = FinishEarlyDL;
export const ResetMatchTugOfWarDL = ResetMatchDL;
export const GetMatchStateTugOfWarDL = GetMatchStateDL;
export const SendTapBatchTugOfWarDL = SendTapBatchDL;
export const RegisterFanTugOfWarDL = RegisterFanDL;
export const UnregisterFanTugOfWarDL = UnregisterFanDL;

export default {
    getFanClientId,
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartCountdownDL,
    PauseMatchDL,
    ResumeMatchDL,
    FinishEarlyDL,
    ResetMatchDL,
    GetMatchStateDL,
    SendTapBatchDL,
    RegisterFanDL,
    UnregisterFanDL,
    GetThemesTugOfWarDL,
    GetThemeByIdTugOfWarDL,
    CreateThemeTugOfWarDL,
    UpdateThemeTugOfWarDL,
    DeleteThemeTugOfWarDL,
    CloneThemeTugOfWarDL,
    GetConfigTugOfWarDL,
    SaveConfigTugOfWarDL,
    GetMatchHistoryTugOfWarDL,
    SaveMatchResultTugOfWarDL,
    UploadAssetTugOfWarDL,
    UploadBatchMediaTugOfWarDL,
    ShowCoverTugOfWarDL,
    ShowGameSceneTugOfWarDL,
    StartCountdownTugOfWarDL,
    PauseMatchTugOfWarDL,
    ResumeMatchTugOfWarDL,
    FinishEarlyTugOfWarDL,
    ResetMatchTugOfWarDL,
    GetMatchStateTugOfWarDL,
    SendTapBatchTugOfWarDL,
    RegisterFanTugOfWarDL,
    UnregisterFanTugOfWarDL
};
